import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import {
  BarChart3,
  FileText,
  Download,
  FileSpreadsheet,
  FileType,
  Loader2,
  Calendar,
  Users,
  Ticket,
  Package,
  Shield,
  Building2,
  ClipboardList,
  Filter,
  RefreshCw,
  Eye,
  Printer,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  User,
  Mail,
  Phone,
  Briefcase,
  MessageSquare,
  DollarSign,
  Boxes,
  UserRoundCheck,
  FileCheck,
  Settings,
  Brain,
  History,
  Wallet,
  Receipt,
  Car,
  Plane,
  Heart,
  Home,
  Truck,
  Database,
  Pill
} from "lucide-react";

// Reportes predefinidos base por módulo y submenú
// El key puede ser el ID del menú principal o del submenú específico
const BASE_REPORTES = {
  default: [
    { id: "listado_general", nombre: "Listado General", descripcion: "Lista completa de registros del módulo", icon: ClipboardList },
    { id: "registros_periodo", nombre: "Registros por Período", descripcion: "Registros filtrados por rango de fechas", icon: Calendar },
    { id: "resumen_estadisticas", nombre: "Resumen Estadístico", descripcion: "Estadísticas generales del módulo", icon: BarChart3 },
  ],
  dashboard_ejecutivo: [
    { id: "kpis_generales", nombre: "KPIs Generales", descripcion: "Indicadores clave de rendimiento", icon: TrendingUp },
    { id: "resumen_modulos", nombre: "Resumen por Módulos", descripcion: "Estado general de todos los módulos", icon: BarChart3 },
  ],
  riesgos_generales: [
    { id: "comparaciones_periodo", nombre: "Comparaciones por Período", descripcion: "Comparaciones de pólizas realizadas por rango de fechas", icon: BarChart3 },
    { id: "documentos_subidos", nombre: "Documentos Subidos", descripcion: "Registro de documentos cargados al sistema", icon: FileText },
    { id: "polizas_por_vencer", nombre: "Pólizas por Vencer", descripcion: "Listado de pólizas próximas a vencer", icon: AlertTriangle },
  ],
  // Gestión Humana - reportes por submenú
  gestion_humana: [
    { id: "resumen_gh", nombre: "Resumen General GH", descripcion: "Estado general de Gestión Humana", icon: BarChart3, requiereSubmenu: null },
  ],
  empleados: [
    { id: "empleados_departamento", nombre: "Empleados por Departamento", descripcion: "Lista de empleados agrupados por departamento", icon: Building2 },
    { id: "empleados_activos", nombre: "Empleados Activos", descripcion: "Lista de empleados activos del sistema", icon: Users },
    { id: "cumpleanos_mes", nombre: "Cumpleaños del Mes", descripcion: "Empleados que cumplen años este mes", icon: Calendar },
  ],
  vacaciones: [
    { id: "vacaciones_periodo", nombre: "Vacaciones por Período", descripcion: "Registro de vacaciones solicitadas", icon: Calendar },
    { id: "vacaciones_pendientes", nombre: "Vacaciones Pendientes", descripcion: "Solicitudes pendientes de aprobación", icon: Clock },
    { id: "balance_vacaciones", nombre: "Balance de Vacaciones", descripcion: "Días disponibles por empleado", icon: BarChart3 },
  ],
  nomina: [
    { id: "nomina_mensual", nombre: "Nómina Mensual", descripcion: "Reporte de nómina por mes", icon: Wallet },
    { id: "nomina_periodo", nombre: "Nómina por Período", descripcion: "Resumen de nómina en rango de fechas", icon: Calendar },
  ],
  solicitudes_gh: [
    { id: "solicitudes_periodo", nombre: "Solicitudes por Período", descripcion: "Todas las solicitudes de GH", icon: ClipboardList },
    { id: "permisos_solicitados", nombre: "Permisos Solicitados", descripcion: "Reporte de permisos", icon: FileCheck },
  ],
  movimientos_gh: [
    { id: "movimientos_periodo", nombre: "Movimientos por Período", descripcion: "Historial de movimientos de GH", icon: History },
  ],
  reclutamiento: [
    { id: "vacantes_activas", nombre: "Vacantes Activas", descripcion: "Posiciones abiertas", icon: Briefcase },
    { id: "candidatos_proceso", nombre: "Candidatos en Proceso", descripcion: "Candidatos por etapa", icon: Users },
  ],
  riesgos_personas: [
    { id: "polizas_vida", nombre: "Pólizas de Vida", descripcion: "Listado de pólizas de vida activas", icon: Heart },
    { id: "beneficiarios", nombre: "Reporte de Beneficiarios", descripcion: "Beneficiarios registrados por póliza", icon: Users },
  ],
  // Mensajería
  mensajeria: [
    { id: "solicitudes_periodo", nombre: "Solicitudes por Período", descripcion: "Lista de solicitudes con fechas de asignación y entrega", icon: Package },
    { id: "pendientes_mensajero", nombre: "Reporte de Pendientes", descripcion: "Detalle de entregas pendientes por mensajero con días transcurridos", icon: Clock },
    { id: "solicitudes_mensajero", nombre: "Entregas por Mensajero", descripcion: "Entregas realizadas por cada mensajero", icon: Users },
    { id: "urgentes_pendientes", nombre: "Urgentes Pendientes", descripcion: "Solicitudes urgentes sin completar", icon: AlertTriangle },
  ],
  // Mesa de Ayuda
  mesa_ayuda: [
    { id: "tickets_periodo", nombre: "Tickets por Período", descripcion: "Lista de tickets por rango de fechas", icon: Ticket },
    { id: "tickets_categoria", nombre: "Tickets por Categoría", descripcion: "Resumen de tickets por categoría", icon: ClipboardList },
    { id: "tiempo_resolucion", nombre: "Tiempo de Resolución", descripcion: "Análisis de tiempos de resolución", icon: Clock },
  ],
  tickets: [
    { id: "tickets_periodo", nombre: "Tickets por Período", descripcion: "Lista de tickets por rango de fechas", icon: Ticket },
    { id: "tickets_categoria", nombre: "Tickets por Categoría", descripcion: "Resumen de tickets por categoría", icon: ClipboardList },
  ],
  mis_tickets: [
    { id: "mis_tickets_periodo", nombre: "Mis Tickets por Período", descripcion: "Mis tickets por rango de fechas", icon: Ticket },
  ],
  // WhatsApp
  whatsapp: [
    { id: "conversaciones_periodo", nombre: "Conversaciones por Período", descripcion: "Historial de conversaciones", icon: MessageSquare },
    { id: "tiempo_respuesta", nombre: "Tiempo de Respuesta", descripcion: "Análisis de tiempos de respuesta", icon: Clock },
  ],
  // Finanzas
  finanzas_contabilidad: [
    { id: "resumen_finanzas", nombre: "Resumen Financiero", descripcion: "Estado general de finanzas", icon: DollarSign },
  ],
  inventario: [
    { id: "inventario_actual", nombre: "Inventario Actual", descripcion: "Listado completo de artículos con stock", icon: Boxes },
    { id: "stock_bajo", nombre: "Artículos con Stock Bajo", descripcion: "Artículos por debajo del stock mínimo", icon: AlertTriangle },
    { id: "movimientos_inventario", nombre: "Movimientos de Inventario", descripcion: "Entradas y salidas de inventario", icon: TrendingUp },
  ],
  mobiliario: [
    { id: "mobiliario_activos", nombre: "Activos de Mobiliario", descripcion: "Listado de activos fijos", icon: Briefcase },
    { id: "mobiliario_asignaciones", nombre: "Asignaciones de Mobiliario", descripcion: "Activos asignados por usuario", icon: Users },
  ],
  caja_chica: [
    { id: "caja_chica_transacciones", nombre: "Transacciones Caja Chica", descripcion: "Movimientos de caja chica", icon: Wallet },
    { id: "caja_chica_periodo", nombre: "Caja Chica por Período", descripcion: "Gastos por rango de fechas", icon: Calendar },
  ],
  medicamentos: [
    { id: "medicamentos_stock", nombre: "Stock de Medicamentos", descripcion: "Inventario de medicamentos", icon: Pill },
    { id: "medicamentos_vencimiento", nombre: "Por Vencer", descripcion: "Medicamentos próximos a vencer", icon: AlertTriangle },
  ],
  cuentas_pagar: [
    { id: "facturas_pendientes", nombre: "Facturas Pendientes", descripcion: "Facturas por pagar", icon: Receipt },
    { id: "facturas_periodo", nombre: "Facturas por Período", descripcion: "Facturas en rango de fechas", icon: Calendar },
  ],
  // Control de Visitantes
  control_visitantes: [
    { id: "visitantes_periodo", nombre: "Visitantes por Período", descripcion: "Registro de visitantes", icon: UserRoundCheck },
    { id: "visitantes_tipo", nombre: "Visitantes por Tipo", descripcion: "Visitantes agrupados por tipo", icon: ClipboardList },
    { id: "tiempo_permanencia", nombre: "Tiempo de Permanencia", descripcion: "Análisis de permanencia", icon: Clock },
  ],
  // Requisiciones
  requisiciones: [
    { id: "requisiciones_periodo", nombre: "Requisiciones por Período", descripcion: "Lista de requisiciones", icon: ClipboardList },
    { id: "requisiciones_estado", nombre: "Requisiciones por Estado", descripcion: "Resumen por estado", icon: FileCheck },
    { id: "items_solicitados", nombre: "Items Más Solicitados", descripcion: "Artículos más solicitados", icon: TrendingUp },
  ],
  // Reportes
  reportes: [
    { id: "reportes_generados", nombre: "Reportes Generados", descripcion: "Historial de reportes generados", icon: FileText },
    { id: "comparaciones_sistema", nombre: "Comparaciones del Sistema", descripcion: "Comparaciones guardadas", icon: BarChart3 },
  ],
  // Configuración
  configuracion: [
    { id: "auditoria_cambios", nombre: "Auditoría de Cambios", descripcion: "Registro de cambios en el sistema", icon: History },
    { id: "usuarios_sistema", nombre: "Usuarios del Sistema", descripcion: "Listado de usuarios", icon: Users },
    { id: "departamentos_activos", nombre: "Departamentos Activos", descripcion: "Listado de departamentos", icon: Building2 },
  ],
  usuarios: [
    { id: "usuarios_sistema", nombre: "Usuarios del Sistema", descripcion: "Listado de usuarios activos", icon: Users },
    { id: "usuarios_departamento", nombre: "Usuarios por Departamento", descripcion: "Usuarios agrupados por área", icon: Building2 },
  ],
  departamentos: [
    { id: "departamentos_activos", nombre: "Departamentos Activos", descripcion: "Listado de departamentos", icon: Building2 },
  ]
};

// Icon mapping for dynamic menus
const ICON_MAP = {
  "BarChart2": BarChart3,
  "BarChart3": BarChart3,
  "Shield": Shield,
  "Users": Users,
  "Package": Package,
  "Ticket": Ticket,
  "MessageSquare": MessageSquare,
  "DollarSign": DollarSign,
  "UserRoundCheck": UserRoundCheck,
  "ClipboardList": ClipboardList,
  "FileText": FileText,
  "Settings": Settings,
  "Heart": Heart,
  "Car": Car,
  "Boxes": Boxes,
  "Building2": Building2,
  "Database": Database,
  "Briefcase": Briefcase,
  "Wallet": Wallet
};

export default function ReportesSistemaPage() {
  const [activeTab, setActiveTab] = useState("predefinidos");
  const [selectedModulo, setSelectedModulo] = useState("");
  const [selectedReporte, setSelectedReporte] = useState(null);
  const [reporteData, setReporteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Dynamic modules from menus
  const [modulosDisponibles, setModulosDisponibles] = useState([]);
  const [loadingModulos, setLoadingModulos] = useState(true);
  
  // Filtros
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  
  const { getAuthHeaders, user } = useAuth();
  
  // Check if user is admin
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  // Helper function to check if user has access to a menu
  const hasMenuAccess = useCallback((menuId) => {
    // Admins have access to everything
    if (isAdmin) return true;
    
    // If user has no menu_permissions defined at all, allow access to all menus
    // This is the permissive default - permissions restrict, not grant
    if (!user?.menu_permissions || Object.keys(user.menu_permissions).length === 0) {
      return true;
    }
    
    // If user has menu_permissions defined, check for explicit access
    const permission = user.menu_permissions[menuId];
    
    // If this specific menu is not in permissions, check if denied explicitly
    if (permission === undefined) {
      // Check if there's a permisos_denegados field
      const denied = user?.permisos_denegados?.[menuId];
      if (denied?.access === false) return false;
      // If not explicitly denied and not in permissions, allow by default
      return true;
    }
    
    // Check various permission formats
    if (typeof permission === 'boolean') return permission;
    if (typeof permission === 'object') {
      return permission.access === true || permission.view === true || permission.enabled === true;
    }
    
    return false;
  }, [user, isAdmin]);

  // Load modules dynamically from system menus, filtered by user permissions
  useEffect(() => {
    const fetchModulos = async () => {
      try {
        setLoadingModulos(true);
        const res = await axios.get(`${API}/system/menus`, getAuthHeaders());
        const menus = res.data || [];
        
        // Filter menus based on user permissions and convert to module options
        const modulos = menus
          .filter(menu => {
            // Check if user has access to this main menu
            if (!hasMenuAccess(menu.id)) return false;
            
            // Also check if the menu has any submenus the user can access
            if (menu.submenus && menu.submenus.length > 0) {
              const hasAnySubmenuAccess = menu.submenus.some(sub => hasMenuAccess(sub.id));
              // If the menu has submenus, user must have access to at least one
              return hasAnySubmenuAccess;
            }
            
            return true;
          })
          .map(menu => ({
            id: menu.id,
            label: menu.label,
            icon: ICON_MAP[menu.icon] || FileText,
            submenus: menu.submenus?.filter(sub => hasMenuAccess(sub.id)) || []
          }));
        
        setModulosDisponibles(modulos);
        
        // Set first module as default
        if (modulos.length > 0 && !selectedModulo) {
          setSelectedModulo(modulos[0].id);
        }
      } catch (err) {
        console.error("Error loading modules:", err);
        toast.error("Error al cargar módulos");
      } finally {
        setLoadingModulos(false);
      }
    };
    
    fetchModulos();
  }, [getAuthHeaders, hasMenuAccess]);

  // Get the selected module object with its submenus
  const getSelectedModuloData = useCallback(() => {
    return modulosDisponibles.find(m => m.id === selectedModulo);
  }, [modulosDisponibles, selectedModulo]);

  // Get reports for selected module - includes main module reports and accessible submenu reports
  const getReportesModulo = useCallback(() => {
    const moduloData = getSelectedModuloData();
    if (!moduloData) return BASE_REPORTES.default;
    
    let reportes = [];
    
    // Add reports for the main module if available
    if (BASE_REPORTES[selectedModulo]) {
      reportes = [...BASE_REPORTES[selectedModulo]];
    }
    
    // Add reports for each accessible submenu
    if (moduloData.submenus && moduloData.submenus.length > 0) {
      moduloData.submenus.forEach(submenu => {
        if (BASE_REPORTES[submenu.id]) {
          // Add submenu name as category
          const submenuReportes = BASE_REPORTES[submenu.id].map(r => ({
            ...r,
            categoria: submenu.label,
            submenuId: submenu.id
          }));
          reportes = [...reportes, ...submenuReportes];
        }
      });
    }
    
    // If no specific reports found, return defaults
    if (reportes.length === 0) {
      return BASE_REPORTES.default;
    }
    
    return reportes;
  }, [selectedModulo, getSelectedModuloData]);

  // Generar reporte predefinido
  const generarReporte = async (reporte) => {
    setLoading(true);
    setSelectedReporte(reporte);
    
    try {
      const params = new URLSearchParams();
      if (fechaInicio) params.append("fecha_inicio", fechaInicio);
      if (fechaFin) params.append("fecha_fin", fechaFin);
      if (estadoFilter !== "all") params.append("estado", estadoFilter);
      
      // Use submenu ID if available, otherwise use selected module
      const moduloId = reporte.submenuId || selectedModulo;
      
      const res = await axios.get(
        `${API}/reportes/${moduloId}/${reporte.id}?${params.toString()}`,
        getAuthHeaders()
      );
      
      setReporteData(res.data);
      setShowPreview(true);
    } catch (err) {
      console.error("Error generando reporte:", err);
      toast.error("Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  };

  // Exportar reporte
  const exportarReporte = async (formato) => {
    if (!reporteData) return;
    
    try {
      const res = await axios.post(
        `${API}/reportes/exportar`,
        {
          data: reporteData.data,
          columns: reporteData.columns,
          titulo: selectedReporte?.nombre || "Reporte",
          formato: formato
        },
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const extensiones = { excel: 'xlsx', pdf: 'pdf', word: 'docx' };
      a.download = `${selectedReporte?.nombre || 'reporte'}_${new Date().toISOString().split('T')[0]}.${extensiones[formato]}`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte exportado a ${formato.toUpperCase()}`);
    } catch (err) {
      console.error("Error exportando:", err);
      toast.error("Error al exportar el reporte");
    }
  };

  const moduloLabels = {};
  modulosDisponibles.forEach(m => {
    moduloLabels[m.id] = { label: m.label, icon: m.icon };
  });

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="reportes-sistema-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0C314C] dark:text-white flex items-center gap-3">
              <BarChart3 className="w-8 h-8" />
              Centro de Reportes
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Genera reportes predefinidos o crea reportes personalizados
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1">
            <TabsTrigger 
              value="predefinidos" 
              className="data-[state=active]:bg-[#0C314C] data-[state=active]:text-white"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Reportes Predefinidos
            </TabsTrigger>
            <TabsTrigger 
              value="personalizados"
              className="data-[state=active]:bg-[#0C314C] data-[state=active]:text-white"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Reportes Personalizados
            </TabsTrigger>
          </TabsList>

          {/* Tab: Reportes Predefinidos */}
          <TabsContent value="predefinidos" className="space-y-6">
            {/* Filtros globales */}
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#0C314C]" />
                  Filtros
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-slate-400">Módulo</Label>
                    <Select value={selectedModulo} onValueChange={setSelectedModulo}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleccionar módulo" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingModulos ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin" />
                          </div>
                        ) : (
                          modulosDisponibles.map((modulo) => {
                            const Icon = modulo.icon;
                            return (
                              <SelectItem key={modulo.id} value={modulo.id}>
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  {modulo.label}
                                </div>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-slate-400">Fecha Inicio</Label>
                    <Input 
                      type="date" 
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-slate-400">Fecha Fin</Label>
                    <Input 
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-slate-400">Estado</Label>
                    <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="en_proceso">En Proceso</SelectItem>
                        <SelectItem value="completado">Completado</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de reportes del módulo seleccionado - Agrupados por submenú */}
            {(() => {
              const reportes = getReportesModulo();
              const reportesSinCategoria = reportes.filter(r => !r.categoria);
              const reportesConCategoria = reportes.filter(r => r.categoria);
              
              // Agrupar por categoría
              const categorias = [...new Set(reportesConCategoria.map(r => r.categoria))];
              
              return (
                <div className="space-y-6">
                  {/* Reportes del módulo principal (sin categoría) */}
                  {reportesSinCategoria.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#0C314C]" />
                        Reportes Generales
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {reportesSinCategoria.map((reporte) => {
                          const Icon = reporte.icon;
                          return (
                            <Card 
                              key={reporte.id}
                              className="border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow cursor-pointer group"
                              onClick={() => generarReporte(reporte)}
                            >
                              <CardContent className="p-6">
                                <div className="flex items-start gap-4">
                                  <div className="p-3 rounded-lg bg-[#0C314C]/10 dark:bg-[#0C314C]/30 group-hover:bg-[#0C314C] transition-colors">
                                    <Icon className="w-6 h-6 text-[#0C314C] dark:text-[#45E69A] group-hover:text-white transition-colors" />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#0C314C] dark:group-hover:text-[#45E69A] transition-colors">
                                      {reporte.nombre}
                                    </h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                      {reporte.descripcion}
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-4 flex items-center justify-end gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-[#0C314C] hover:text-[#0C314C] hover:bg-[#0C314C]/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      generarReporte(reporte);
                                    }}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Generar
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Reportes agrupados por submenú/categoría */}
                  {categorias.map(categoria => (
                    <div key={categoria}>
                      <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#45E69A]" />
                        {categoria}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {reportesConCategoria
                          .filter(r => r.categoria === categoria)
                          .map((reporte) => {
                            const Icon = reporte.icon;
                            return (
                              <Card 
                                key={`${reporte.submenuId}-${reporte.id}`}
                                className="border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow cursor-pointer group"
                                onClick={() => generarReporte(reporte)}
                              >
                                <CardContent className="p-6">
                                  <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-lg bg-[#45E69A]/10 dark:bg-[#45E69A]/30 group-hover:bg-[#45E69A] transition-colors">
                                      <Icon className="w-6 h-6 text-[#0C314C] dark:text-[#45E69A] group-hover:text-white transition-colors" />
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#0C314C] dark:group-hover:text-[#45E69A] transition-colors">
                                        {reporte.nombre}
                                      </h3>
                                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                        {reporte.descripcion}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="mt-4 flex items-center justify-end gap-2">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-[#0C314C] hover:text-[#0C314C] hover:bg-[#0C314C]/10"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        generarReporte(reporte);
                                      }}
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      Generar
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                  
                  {reportes.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay reportes predefinidos para este módulo</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* Tab: Reportes Personalizados - Placeholder para la próxima implementación */}
          <TabsContent value="personalizados" className="space-y-6">
            <Card className="border-slate-200 dark:border-slate-700">
              <CardContent className="p-12 text-center">
                <FileSpreadsheet className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 dark:text-slate-300 mb-2">
                  Constructor de Reportes Personalizados
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4 max-w-md mx-auto">
                  Arrastra y suelta campos para crear reportes personalizados con la información que necesites
                </p>
                <Button 
                  className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white"
                  onClick={() => window.location.href = '/reportes/constructor'}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Abrir Constructor
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog de Preview de Reporte */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#0C314C]" />
                {selectedReporte?.nombre}
              </DialogTitle>
              <DialogDescription>
                {reporteData?.total || 0} registros encontrados
                {reporteData?.generado_por && (
                  <span className="ml-2">• Generado por: {reporteData.generado_por}</span>
                )}
              </DialogDescription>
            </DialogHeader>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
                <span className="ml-2 text-slate-500">Generando reporte...</span>
              </div>
            ) : reporteData ? (
              <>
                {/* Toolbar de exportación */}
                <div className="flex items-center gap-2 py-3 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-sm text-slate-500 mr-2">Exportar:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportarReporte('excel')}
                    className="border-green-300 text-green-600 hover:bg-green-50"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportarReporte('pdf')}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <FileType className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportarReporte('word')}
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Word
                  </Button>
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => generarReporte(selectedReporte)}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Actualizar
                  </Button>
                </div>

                {/* Tabla de datos */}
                <ScrollArea className="flex-1 max-h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white dark:bg-slate-900">
                      <TableRow>
                        {reporteData.columns?.map((col, idx) => (
                          <TableHead key={idx} className="font-semibold text-slate-700 dark:text-slate-300">
                            {col.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reporteData.data?.length === 0 ? (
                        <TableRow>
                          <TableCell 
                            colSpan={reporteData.columns?.length || 1} 
                            className="text-center py-8 text-slate-500"
                          >
                            No se encontraron registros con los filtros aplicados
                          </TableCell>
                        </TableRow>
                      ) : (
                        reporteData.data?.map((row, rowIdx) => (
                          <TableRow key={rowIdx} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                            {reporteData.columns?.map((col, colIdx) => (
                              <TableCell key={colIdx} className="text-slate-600 dark:text-slate-400">
                                {col.key === 'estado' ? (
                                  <Badge 
                                    variant="outline"
                                    className={
                                      row[col.key] === 'completado' ? 'bg-green-100 text-green-700 border-green-300' :
                                      row[col.key] === 'en_proceso' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                      row[col.key] === 'pendiente' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                      row[col.key] === 'cancelado' ? 'bg-red-100 text-red-700 border-red-300' :
                                      ''
                                    }
                                  >
                                    {row[col.key]}
                                  </Badge>
                                ) : col.key === 'es_urgente' ? (
                                  row[col.key] ? (
                                    <Badge className="bg-red-500 text-white">URGENTE</Badge>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )
                                ) : col.type === 'date' && row[col.key] ? (
                                  new Date(row[col.key]).toLocaleDateString('es-ES')
                                ) : col.type === 'datetime' && row[col.key] ? (
                                  new Date(row[col.key]).toLocaleString('es-ES')
                                ) : (
                                  row[col.key] || '-'
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>

                {/* Resumen */}
                {reporteData.resumen && (
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Resumen</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(reporteData.resumen).map(([key, value]) => (
                        <div key={key} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                          <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                            {key.replace(/_/g, ' ')}
                          </p>
                          <p className="text-lg font-bold text-slate-900 dark:text-white">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
